import db from '@/db';
import { loadServerConfig } from '@/lib/serverConfig';

// =============================================================
// Helpers WhatsApp — normalização e montagem de links wa.me
// =============================================================

/**
 * Normaliza telefone BR para formato wa.me (somente dígitos, com DDI 55).
 * Aceita: "(21) 99999-0000", "21999990000", "+55 21 9 9999-0000".
 * Retorna string vazia se input inválido (<10 dígitos).
 */
export function normalizeBrPhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length < 10) return '';
  // Se já veio com DDI 55 e 12-13 dígitos, retorna como está
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) return digits;
  // Caso contrário, prefixa 55 (Brasil)
  return `55${digits}`;
}

/**
 * Formata telefone BR para exibição: (21) 99999-0000.
 */
export function formatBrPhone(raw) {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  const local = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return raw;
}

export async function getRestaurantPhone() {
  try {
    const cfg = await db.config.get('restaurantPhone');
    if (cfg?.value) return cfg.value;
  } catch {}
  // v7.79 — Fallback: o device do CLIENTE não tem essa config no Dexie
  // (restaurantPhone não está na whitelist de sync do Firebase). Busca do
  // servidor (/api/config) e cacheia localmente.
  try {
    const body = await loadServerConfig();
    const phone = body?.config?.restaurantPhone || '';
    if (phone) {
      try { await db.config.put({ key: 'restaurantPhone', value: phone }); } catch {}
    }
    return phone;
  } catch {
    return '';
  }
}

/**
 * v7.79 — Abre o WhatsApp de forma confiável em mobile. window.open após um
 * `await` é bloqueado pelo popup blocker (iOS Safari) — se bloquear, navega
 * na mesma aba (o estado do checkout sobrevive via localStorage v7.78).
 */
export function openWhatsApp(url) {
  if (!url) return;
  let win = null;
  try { win = window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
  if (!win) window.location.href = url;
}

export async function getRestaurantGreeting() {
  const cfg = await db.config.get('restaurantGreeting');
  return cfg?.value || 'Olá! Preciso de ajuda com meu pedido no Kaven.';
}

/**
 * Monta link wa.me para falar com o restaurante.
 * @param {string} [message] - mensagem pré-preenchida. Se null, usa saudação padrão.
 */
export async function buildRestaurantWhatsAppLink(message) {
  const phone = await getRestaurantPhone();
  const normalized = normalizeBrPhone(phone);
  const text = message || (await getRestaurantGreeting());
  if (!normalized) {
    // Sem número configurado → usa wa.me/?text= (cliente escolhe destinatário)
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }
  return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
}

// =============================================================
// Mensagens automáticas por status (cliente)
// =============================================================

const STATUS_MESSAGES = {
  confirmed: {
    icon: '✅',
    title: 'Pedido Confirmado',
    body: (o) => `Olá ${o.customerName}! Recebemos seu pedido #${o.id} no valor de R$ ${Number(o.total || 0).toFixed(2).replace('.', ',')}. Já estamos preparando com carinho!`,
  },
  preparing: {
    icon: '👨‍🍳',
    title: 'Na Cozinha',
    body: (o) => `Oba! Seu pedido #${o.id} entrou na cozinha. Logo menos sai fresquinho!`,
  },
  ready: {
    icon: '📦',
    title: 'Pronto para Despacho',
    body: (o) => `Seu pedido #${o.id} está pronto e aguardando o entregador sair.`,
  },
  delivering: {
    icon: '🛵',
    title: 'Saiu para Entrega',
    body: (o) => `Seu pedido #${o.id} saiu pra entrega! O entregador já está a caminho de ${o.neighborhood || 'seu bairro'}.`,
  },
  delivered: {
    icon: '🎉',
    title: 'Pedido Entregue',
    body: (o) => `Pedido #${o.id} entregue! Obrigado pela preferência, ${o.customerName}. Volte sempre!`,
  },
};

function buildTrackingUrl(orderId) {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/rastreio/${orderId}`;
  }
  return `/rastreio/${orderId}`;
}

export function buildWhatsAppMessage(order, status) {
  const tpl = STATUS_MESSAGES[status];
  if (!tpl) return null;
  const trackingUrl = buildTrackingUrl(order.mpPaymentId || order.id);
  const body = tpl.body(order);
  return `${tpl.icon} *KAVEN - ${tpl.title}*\n\n${body}\n\n📍 Acompanhe em tempo real:\n${trackingUrl}\n\n_Mensagem automática - Kaven Delivery_`;
}

// Mock: simula webhook Firebase/WhatsApp. Loga no console, toast opcional, e persiste em IndexedDB.
export async function sendWhatsAppWebhook(order, status, { toast } = {}) {
  const message = buildWhatsAppMessage(order, status);
  if (!message) return null;

  const payload = {
    to: order.customerPhone || order.phone || '',
    orderId: order.id,
    status,
    message,
    trackingUrl: buildTrackingUrl(order.mpPaymentId || order.id),
    sentAt: new Date().toISOString(),
  };

  // 1) Console webhook simulado
  // eslint-disable-next-line no-console
  console.log('[WHATSAPP WEBHOOK MOCK - CLIENTE]', payload);

  // 2) Persiste histórico local
  try {
    await db.notifications.add(payload);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Falha ao persistir notificação mock:', err);
  }

  // 3) Toast visual opcional (quando chamado do painel Admin)
  if (toast) {
    const tpl = STATUS_MESSAGES[status];
    toast.success(`${tpl.icon} WhatsApp -> ${payload.to || 'cliente'}: ${tpl.title}`);
  }

  return payload;
}


